import React, { useMemo } from 'react';
import {
  Box,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { collection, limit, orderBy, query, where } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { useFirestoreQuery } from '../hooks/useFirestoreQuery';

const ELEVATED = ['company_admin', 'developer', 'manager'];

function parseDate(v) {
  if (!v) return null;
  if (v.toDate) return v.toDate();
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

function refCell(log) {
  if (log.ticketId) return log.ticketId;
  const m = log.meta || {};
  if (m.linkId) return `link:${m.linkId}`;
  if (m.targetUid) return `user:${m.targetUid}`;
  return '—';
}

/**
 * Company-wide activity stream (Firestore `ticketLogs` — tickets, links, team, etc.).
 */
export default function CompanyActivityLogPanel({ userData, maxRows = 200 }) {
  const companyId = userData?.companyId;
  const role = userData?.role;
  const uid = auth.currentUser?.uid;
  const wide = ELEVATED.includes(role);

  const logsQuery = useMemo(() => {
    if (!companyId || !uid) return null;
    if (wide) {
      return query(
        collection(db, 'ticketLogs'),
        where('companyId', '==', companyId),
        orderBy('createdAt', 'desc'),
        limit(maxRows),
      );
    }
    return query(
      collection(db, 'ticketLogs'),
      where('companyId', '==', companyId),
      where('participantUids', 'array-contains', uid),
      orderBy('createdAt', 'desc'),
      limit(maxRows),
    );
  }, [companyId, uid, wide, maxRows]);

  const { data: logs, loading } = useFirestoreQuery(logsQuery, {
    enabled: Boolean(companyId && uid),
  });

  if (!companyId) {
    return <Typography color="text.secondary">No company assigned.</Typography>;
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Activity log</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 720 }}>
        {wide
          ? 'All recorded events for your organization: tickets, external links, team changes, and more.'
          : 'Events for tickets you are involved in and other actions that include you.'}
      </Typography>
      <Paper variant="outlined" sx={{ maxHeight: 560, overflow: 'auto' }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>When</TableCell>
              <TableCell>Action</TableCell>
              <TableCell>Summary</TableCell>
              <TableCell>Ref</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={4}>
                  <Typography variant="body2" color="text.secondary">Loading…</Typography>
                </TableCell>
              </TableRow>
            )}
            {!loading && logs.length === 0 && (
              <TableRow>
                <TableCell colSpan={4}>
                  <Typography variant="body2" color="text.secondary">No activity yet.</Typography>
                </TableCell>
              </TableRow>
            )}
            {!loading && logs.map((log) => {
              const when = parseDate(log.createdAt);
              return (
                <TableRow key={log.id} hover>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {when ? when.toLocaleString() : '—'}
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={log.action || 'event'} variant="outlined" />
                  </TableCell>
                  <TableCell>{log.summary || '—'}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                    {refCell(log)}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
}
